import React from 'react';
import { ShimmerBox } from '../PageSkeleton/PageSkeleton';

const PathologyReportsSkeleton: React.FC = () => (
    <div className="flex flex-col gap-3 animate-fade-in-up">

        {/* ── CONTEXTUAL STRIP (matches flex items-center gap-3 flex-wrap) ── */}
        <div className="flex items-center gap-3 flex-wrap" style={{ minHeight: 40 }}>
            {/* STUDY label */}
            <ShimmerBox className="h-3 w-9 rounded" delay="0s" />
            {/* Study dropdown */}
            <ShimmerBox className="h-9 w-64 rounded-xl" delay="0.02s" />
            {/* Upload Report button (ml-auto) */}
            <div className="ml-auto">
                <ShimmerBox className="h-9 w-32 rounded-xl" delay="0.04s" />
            </div>
        </div>

        {/* ── MOBILE TAB SWITCHER (lg:hidden) — always shimmer as if present ── */}
        <div className="flex lg:hidden rounded-xl border border-[var(--v2-border-card)] bg-[var(--v2-bg-surface)] p-1 gap-1">
            <ShimmerBox className="flex-1 h-9 rounded-lg" delay="0.06s" />
            <ShimmerBox className="flex-1 h-9 rounded-lg" delay="0.08s" />
        </div>

        {/* ── 2-PANEL ROW ── */}
        <div className="flex flex-col lg:flex-row gap-4 lg:items-stretch items-start" style={{ minHeight: 480 }}>

            {/* LEFT PANEL — Report list */}
            <div
                className="w-full lg:w-72 xl:w-80 flex-shrink-0 rounded-2xl border border-[var(--v2-border-card)] bg-[var(--v2-bg-card)] shadow-card overflow-hidden flex flex-col"
                style={{ minHeight: 320 }}
            >
                {/* Panel label row — matches: FileText icon · "REPORTS" label · count badge */}
                <div className="flex items-center gap-2 px-4 py-3 border-b border-[var(--v2-border-subtle)]">
                    <ShimmerBox className="w-3.5 h-3.5 rounded flex-shrink-0" delay="0.08s" />
                    <ShimmerBox className="h-2.5 w-14 rounded" delay="0.1s" />
                    <ShimmerBox className="w-5 h-5 rounded-full ml-auto" delay="0.12s" />
                </div>

                {/* Report rows — badge + version + date / filename / Open·Replace·Delete */}
                <div className="flex-1 flex flex-col">
                    {Array.from({ length: 4 }).map((_, i) => (
                        <div
                            key={i}
                            className="relative px-4 py-3.5 border-b border-[var(--v2-border-divider)] last:border-0"
                        >
                            {/* Selection bar */}
                            <div className="absolute left-0 top-3 bottom-3 w-[3px] rounded-r bg-[var(--v2-border-subtle)]" />

                            {/* Badge + version tag + date */}
                            <div className="flex items-center gap-2 mb-1.5">
                                <ShimmerBox className="h-[18px] w-10 rounded-md" delay={`${i * 0.04}s`} />
                                <ShimmerBox className="h-[18px] w-7 rounded-md" delay={`${i * 0.04 + 0.01}s`} />
                                <ShimmerBox className="h-2.5 w-24 rounded ml-auto" delay={`${i * 0.04 + 0.02}s`} />
                            </div>

                            {/* Filename */}
                            <ShimmerBox
                                className="h-3 rounded mb-2"
                                style={{ width: `${56 + (i % 4) * 10}%` }}
                                delay={`${i * 0.04 + 0.03}s`}
                            />

                            {/* Action buttons — Open · Replace · Delete icon */}
                            <div className="flex items-center gap-1">
                                <ShimmerBox className="h-6 w-14 rounded-lg" delay={`${i * 0.04 + 0.04}s`} />
                                <ShimmerBox className="h-6 w-18 rounded-lg" delay={`${i * 0.04 + 0.05}s`} />
                                <ShimmerBox className="h-6 w-7 rounded-lg ml-auto" delay={`${i * 0.04 + 0.06}s`} />
                            </div>
                        </div>
                    ))}
                </div>
            </div>

            {/* RIGHT PANEL — Preview */}
            <div
                className="flex-1 min-w-0 rounded-2xl border border-[var(--v2-border-card)] bg-[var(--v2-bg-card)] shadow-card overflow-hidden flex flex-col"
                style={{ minHeight: 400 }}
            >
                {/* Accent gradient line — 3px */}
                <div
                    className="h-[3px] flex-shrink-0"
                    style={{
                        background: 'linear-gradient(90deg,var(--v2-border-subtle) 0%,var(--v2-border-card) 60%,transparent 100%)',
                    }}
                />

                {/* Meta strip — badge · filename · date · Open button */}
                <div className="flex items-center gap-2.5 px-5 pt-4 pb-3 flex-shrink-0">
                    <ShimmerBox className="h-[18px] w-10 rounded-md" delay="0.06s" />
                    <ShimmerBox className="h-3.5 flex-1 max-w-xs rounded" delay="0.08s" />
                    <ShimmerBox className="h-3 w-28 rounded hidden sm:block" delay="0.1s" />
                    <ShimmerBox className="h-7 w-16 rounded-lg flex-shrink-0" delay="0.12s" />
                </div>

                {/* Hairline divider */}
                <div className="mx-5 h-px bg-[var(--v2-border-subtle)] mb-4 flex-shrink-0" />

                {/* Preview area — large shimmer block fills remaining height */}
                <div className="flex-1 min-h-0 px-5 pb-5">
                    <ShimmerBox className="w-full h-full rounded-xl" delay="0.14s" style={{ minHeight: 280 }} />
                </div>
            </div>
        </div>
    </div>
);

export default PathologyReportsSkeleton;
